import { Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {environment} from "../../../environments/environment";
import {map} from "rxjs/operators";
import {AdRequestOptions, AdRequestParamsAttributionOverview} from '../interfaces/ad-reports';
import {
  AttributionOverviewData,
  AttributionOverviewDataItem,
  AttributionOverviewReportData, AttributionOverviewGenerateResponse
} from '../store/quickDashboard/attribution-overview';
import {Site} from '../interfaces/site';
import {Serializer} from '../interfaces/serializer';

@Injectable({
  providedIn: 'root'
})
export class AttributionOverviewService {
  private readonly _channels: Array<string> = [
    '1_chan',
    '2_chan',
    '3_chan',
    '4_chan',
    '5_chan',
    '6_chan',
    '7_chan',
    '8_chan',
    '9_chan',
    '10_chan',
    'more_10_chan'
  ];

  private readonly _touchpoints: Array<string> = [
    '1_touch',
    '2_touch',
    '3_touch',
    '4_touch',
    '5_touch',
    '6_touch',
    '7_touch',
    '8_touch',
    '9_touch',
    '10_touch',
    'more_10_touch'
  ];

  private readonly _time: Array<string> = [
    'less_1_hour',
    'less_12_hours',
    'less_24_hours',
    'less_2_days',
    'less_3_days',
    'less_4_days',
    'less_5_days',
    'less_10_days',
    'less_15_days',
    'less_20_days',
    'less_30_days',
    'more_30_days'
  ];

  constructor(
    private readonly _http: HttpClient
  ) {}

  public generate(
    dataSet: Serializer<Site>,
    params: AdRequestParamsAttributionOverview,
    options: Partial<AdRequestOptions>
  ): Observable<AttributionOverviewReportData> {
    return this._http.post<AttributionOverviewGenerateResponse>(
      `${environment.baseUrl}/api/${dataSet.id}/reports/generate-channel-overview-report`,
      {
        parameters: params,
        options: options
      }
    ).pipe(
      map((report: AttributionOverviewGenerateResponse): AttributionOverviewReportData => {
        return {
          ...this._format(report),
          totalConversions: Math.round(report.all_conv?.nb_conv) || 0,
          conversionMonotouch: Math.round(report.mono_touch?.nb_conv) || 0,
          conversionMultitouch: Math.round(report.multi_touch?.nb_conv) || 0,
          conversionMonotouchPercentage: report.all_conv?.nb_conv && report.mono_touch?.nb_conv / report.all_conv?.nb_conv || 0,
          conversionMultitouchPercentage: report.all_conv?.nb_conv && report.multi_touch?.nb_conv / report.all_conv?.nb_conv || 0,
          conversionMonochannel: Math.round(report.mono_chan?.nb_conv) || 0,
          conversionMultichannel: Math.round(report.multi_chan?.nb_conv) || 0,
          conversionMonochannelPercentage: report.all_conv?.nb_conv && report.mono_chan?.nb_conv / report.all_conv?.nb_conv || 0,
          conversionMultichannelPercentage: report.all_conv?.nb_conv && report.multi_chan?.nb_conv / report.all_conv?.nb_conv || 0,
          allConversionsAvgDaysToConversion: Math.round(report.all_conv?.avg_days_to_conv) || 0,
          allConversionsAvgNumbersOfChannels: Math.round(report.all_conv?.avg_nb_channel) || 0,
          allConversionsAvgNumbersTouchpoints: Math.round(report.all_conv?.avg_nb_touch_points) || 0,
          multitouchpointAvgDaysToConversion: Math.round(report.multi_touch?.avg_days_to_conv) || 0,
          multitouchpointAvgNumbersOfChannels: Math.round(report.multi_touch?.avg_nb_channel) || 0,
          multitouchpointAvgNumbersTouchpoints: Math.round(report.multi_touch?.avg_nb_touch_points) || 0,
          multitouchpointPaidAvgDaysToConversion: Math.round(report.multi_touch_paid?.avg_days_to_conv) || 0,
          multitouchpointPaidAvgNumbersOfChannels: Math.round(report.multi_touch_paid?.avg_nb_channel) || 0,
          multitouchpointPaidAvgNumbersTouchpoints: Math.round(report.multi_touch_paid?.avg_nb_touch_points) || 0
        };
      })
    );
  }

  private _format(report: AttributionOverviewGenerateResponse): { table: AttributionOverviewData, chart: AttributionOverviewData } {
    const data: { table: AttributionOverviewData, chart: AttributionOverviewData } = {
      table: {
        all: {
          time: [],
          touchpoints: [],
          channels: []
        },
        paid: {
          time: [],
          touchpoints: [],
          channels: []
        }
      },
      chart: {
        all: {
          time: [],
          touchpoints: [],
          channels: []
        },
        paid: {
          time: [],
          touchpoints: [],
          channels: []
        }
      }
    };

    this._formatTableAndChartData(report, data,'all', 'time', 'all_conv', 'time_to_conv');
    this._formatTableAndChartData(report, data,'paid','time', 'paid_conv', 'time_to_conv');
    this._formatTableAndChartData(report, data,'all', 'touchpoints', 'all_conv', 'touch_to_conv');
    this._formatTableAndChartData(report, data,'paid', 'touchpoints', 'paid_conv', 'touch_to_conv');
    this._formatTableAndChartData(report, data,'all','channels', 'all_conv', 'chan_to_conv');
    this._formatTableAndChartData(report, data,'paid','channels','paid_conv', 'chan_to_conv');

    return data;
  }

  private _formatTableAndChartData(
    report: AttributionOverviewGenerateResponse,
    data: any,
    type: 'all' | 'paid',
    category: 'time' | 'touchpoints' | 'channels',
    key1: string,
    key2: string
  ): void {
    if (report[key1]?.[key2]) {
      for (const key of this[`_${category}`]) {
        const item: AttributionOverviewDataItem = {
          name: `attribution_overview.${key}`,
          value: Math.round(report[key1][key2][key]),
          formatted_value: report[key1][key2][key],
          percent: report[key1][key2][key] / report[key1].nb_conv
        };

        data.table[type][category].push(item);

        if (report[key1][key2][key]) {
          data.chart[type][category].push(item);
        }
      }
    }
  }

}
